import axios from 'lib/axios'
import { getAccessToken, refreshAccessToken } from 'services/login/auth-session'
export type Audience = 'CLIENT' | 'SUPPORT' | 'TECHNOLOGY'
export type Scope = { scope: 'GLOBAL' | 'COMPANY'; company_id: number | null }
export type Authority = { audiences: Audience[]; edit: boolean; publish: boolean }
export type Capability = Scope & Authority
export interface Access {
  manage_permissions: boolean
  manage_configuration: boolean
  scopes: Capability[]
  companies: Array<{ id: number; name: string }>
  types: Array<{ code: string; name: string }>
}
export interface Resource {
  id: number
  filename: string
  mime: string
  size: number
  markdown_status?: 'PENDING' | 'READY' | 'FAILED'
  markdown_error?: string | null
  markdown_generated_at?: string | null
}
export interface Review {
  id: number
  decision: string
  reason: string
  requested_by: number
  decided_by: number | null
  requested_at: string
  decided_at: string | null
}
export interface Version {
  created_by: number
  updated_by: number
  created_at: string
  updated_at: string
  id: number
  number: number
  title: string
  summary: string
  content: string
  category: string
  type_code: string
  audiences: Audience[]
  state: string
  response_mode: 'STRICT' | 'FLEXIBLE'
  approved_for_ai: boolean
  approved_by: number | null
  approved_at: string | null
  ai_eligible: boolean
  provenance: 'MANUAL' | 'AI_ASSISTED'
  effective_from: string | null
  review_after: string | null
  can_approve: boolean
  resources: Resource[]
  reviews: Review[]
  published_at: string | null
  retired_at: string | null
  replaced_by_version_id: number | null
}
export interface Document extends Scope {
  id: number
  revision: number
  current_version_id: number | null
  capabilities: Authority
  versions: Version[]
}
export interface Item extends Scope {
  id: number
  title: string
  version_id: number
  number: number
  state: string
  category: string
  audiences: Audience[]
}
export interface Audit {
  id: string
  action: string
  actor_id: number
  actor_role: string
  outcome: string
  company_id: number | null
  transversal_access: boolean
  version_id: number | null
  created_at: string
  changes: Record<string, unknown>
}
export interface AssistantCitation {
  document_id: number
  version_id: number
  title: string
  text: string
  score: number | null
}
export interface AssistantQueryResult {
  query: string
  answer: string
  citations: AssistantCitation[]
  company_context: {
    source: 'COMPANY_OPERATIONAL_DATA'
    company_id: number
    company_name: string
    generated_at: string
  } | null
  timing: {
    ai_response_ms: number
    application_processing_ms: number
    compatibility_probe_ms: number
    model_generation_ms: number
    output_tokens: number
    tokens_per_second: number
    gateway_path: string
    think_sent: boolean
    think: boolean
  }
}
export type AiDecision = Pick<Version, 'approved_for_ai' | 'approved_by' | 'approved_at' | 'ai_eligible'> & {
  document_id: number
  version_id: number
  revision: number
}
export interface Permission extends Scope {
  id: number
  user_id: number
  can_edit: boolean
  can_publish: boolean
  readable_audiences: Audience[]
  active: boolean
}
export interface ModuleConfiguration {
  source: 'ENVIRONMENT' | 'LOCAL_PREVIEW' | 'PLATFORM'
  database: {
    configured: boolean
    accessible: boolean
    scope: 'SESSION_COMPANY'
  }
  resources: {
    private_storage_enabled: boolean
    bucket_configured: boolean
    max_size_mib: number
    allowed_types: Array<{ mime: string; label: string }>
  }
  rag: {
    enabled: boolean
    gateway: string | null
    api_key_configured: boolean
    model_id: string | null
    timeout_ms: number
    batch_size: number
    poll_ms: number
    max_attempts: number
    response_profile: {
      audience: string
      style: 'PRACTICAL' | 'BRIEF' | 'TECHNICAL' | 'SUPPORT'
      instructions: string
    }
    agent_policy: {
      role: 'SUPPORT_SPECIALIST' | 'HR_ADVISOR' | 'PLATFORM_ADMIN' | 'END_USER_GUIDE'
      response_format: 'STEP_BY_STEP' | 'INCIDENT_DIAGNOSIS' | 'CHECKLIST' | 'EXPLANATION'
      detail_level: 'BRIEF' | 'STANDARD' | 'DETAILED'
      source_priority: 'PUBLISHED_FIRST' | 'MOST_RECENT' | 'COMPANY_FIRST' | 'TEST_CORPUS'
      min_confidence: number
      clarification_mode: 'ASK' | 'LIMITED_ANSWER'
      citations_required: boolean
      require_evidence: boolean
      rules: string
      internet_enabled: boolean
      company_data_enabled: boolean
      streaming_enabled: boolean
      max_tokens: number
    }
    conversation_policy: {
      courtesy_enabled: boolean
      greeting_type: 'ANY' | 'FORMAL' | 'CLOSE' | 'NONE'
      farewell_type: 'ANY' | 'FORMAL' | 'CLOSE' | 'NONE'
      greeting_formality: number
      farewell_formality: number
      greeting_intent: string
      greeting_response: string
      thanks_response: string
      farewell_response: string
      safety_response: string
      unrecognized_response: string
      blocked_terms: string
    }
    mtls_required: boolean
    mtls_configured: boolean
  }
  tas: {
    enabled: boolean
    endpoint: string | null
    api_key_configured: boolean
    timeout_ms: number
    pedido_id: number | null
    created_by_employee_id: number | null
    responsible_employee_id: number | null
    initial_state: string
  }
}
export type Content = Pick<
  Version,
  | 'title'
  | 'summary'
  | 'content'
  | 'category'
  | 'type_code'
  | 'audiences'
  | 'response_mode'
  | 'provenance'
  | 'effective_from'
  | 'review_after'
>
export const knowledgeApi = {
  async access(): Promise<Access> {
    return (await axios.get('/knowledge/access')).data.data
  },
  async configuration(): Promise<ModuleConfiguration> {
    return (await axios.get('/knowledge/configuration')).data.data
  },
  async storageConnection(): Promise<{ reachable: boolean; bucket: string }> {
    return (await axios.post('/knowledge/configuration/storage-connection')).data.data
  },
  async databaseConnection(): Promise<{ reachable: boolean; source: string; scope: 'SESSION_COMPANY' }> {
    return (await axios.post('/knowledge/configuration/database-connection')).data.data
  },
  async ragModels(data: { rag_gateway_url: string; rag_api_key?: string }): Promise<Array<{ id: string; name: string }>> {
    return (await axios.post('/knowledge/configuration/rag-models', data)).data.data
  },
  async assistantQuery(
    query: string,
    includePreproduction = false,
    think = false,
  ): Promise<AssistantQueryResult> {
    return (await axios.post('/knowledge/assistant/query', {
      query,
      limit: 5,
      include_preproduction: includePreproduction,
      think,
    }, {
      // A local AI diagnostic must report authentication failures in its trace,
      // without terminating the user's active application session.
      preserveSessionOnAuthFailure: true,
    })).data.data
  },
  async assistantQueryStream(
    query: string,
    includePreproduction: boolean,
    think: boolean,
    onDelta: (delta: string) => void,
  ): Promise<AssistantQueryResult> {
    const send = async (token: string): Promise<Response> => fetch(
      `${process.env.REACT_APP_MAIN_API || ''}/knowledge/assistant/stream`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query, limit: 5, include_preproduction: includePreproduction, think }),
      },
    )
    let response = await send(getAccessToken())
    if (response.status === 401 && await refreshAccessToken())
      response = await send(getAccessToken())
    if (!response.ok || !response.body) throw new Error(`HTTP_${response.status}`)

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let completed: AssistantQueryResult | null = null
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const events = buffer.split(/\r?\n\r?\n/)
      buffer = events.pop() || ''
      for (const event of events) {
        const name = event.match(/^event:\s*(.+)$/m)?.[1]?.trim()
        const raw = event.match(/^data:\s*(.+)$/m)?.[1]
        if (!name || !raw) continue
        const data = JSON.parse(raw) as { delta?: string; code?: string } | AssistantQueryResult
        if (name === 'delta' && typeof (data as { delta?: string }).delta === 'string')
          onDelta((data as { delta: string }).delta)
        if (name === 'error') throw new Error((data as { code?: string }).code || 'KNOWLEDGE_OPERATION_FAILED')
        if (name === 'complete') completed = data as AssistantQueryResult
      }
    }
    if (!completed) throw new Error('RAG_GATEWAY_STREAM_INCOMPLETE')
    return completed
  },
  async tasConnection(data: {
    tas_api_base_url: string
    tas_api_key?: string
    tas_timeout_ms: number
    tas_pedido_id: number
  }): Promise<{ reachable: boolean; pedido_id: number }> {
    return (await axios.post('/knowledge/configuration/tas-connection', data)).data.data
  },
  async updateConfiguration(data: {
    rag_enabled: boolean
    rag_gateway_url: string
    rag_model_id: string
    rag_api_key?: string
    rag_timeout_ms: number
    rag_batch_size: number
    rag_poll_ms: number
    rag_max_attempts: number
    rag_max_tokens: number
    rag_response_audience: string
    rag_response_style: 'PRACTICAL' | 'BRIEF' | 'TECHNICAL' | 'SUPPORT'
    rag_response_instructions: string
    rag_agent_role: 'SUPPORT_SPECIALIST' | 'HR_ADVISOR' | 'PLATFORM_ADMIN' | 'END_USER_GUIDE'
    rag_response_format: 'STEP_BY_STEP' | 'INCIDENT_DIAGNOSIS' | 'CHECKLIST' | 'EXPLANATION'
    rag_detail_level: 'BRIEF' | 'STANDARD' | 'DETAILED'
    rag_source_priority: 'PUBLISHED_FIRST' | 'MOST_RECENT' | 'COMPANY_FIRST' | 'TEST_CORPUS'
    rag_min_confidence: number
    rag_clarification_mode: 'ASK' | 'LIMITED_ANSWER'
    rag_citations_required: boolean
    rag_require_evidence: boolean
    rag_agent_rules: string
    rag_internet_enabled: boolean
    rag_company_data_enabled: boolean
    rag_streaming_enabled: boolean
    rag_courtesy_enabled: boolean
    rag_greeting_formality: number
    rag_farewell_formality: number
    rag_greeting_intent: string
    rag_greeting_response: string
    rag_thanks_response: string
    rag_farewell_response: string
    rag_safety_response: string
    rag_unrecognized_response: string
    rag_blocked_terms: string
    tas_enabled: boolean
    tas_api_base_url: string
    tas_api_key?: string
    tas_timeout_ms: number
    tas_pedido_id: number | null
    tas_created_by_employee_id: number | null
    tas_responsible_employee_id: number | null
  }): Promise<ModuleConfiguration> {
    return (await axios.put('/knowledge/configuration', data)).data.data
  },
  async updateAgentPolicy(data: {
    rag_response_audience: string
    rag_response_style: 'PRACTICAL' | 'BRIEF' | 'TECHNICAL' | 'SUPPORT'
    rag_response_instructions: string
    rag_agent_role: 'SUPPORT_SPECIALIST' | 'HR_ADVISOR' | 'PLATFORM_ADMIN' | 'END_USER_GUIDE'
    rag_response_format: 'STEP_BY_STEP' | 'INCIDENT_DIAGNOSIS' | 'CHECKLIST' | 'EXPLANATION'
    rag_detail_level: 'BRIEF' | 'STANDARD' | 'DETAILED'
    rag_source_priority: 'PUBLISHED_FIRST' | 'MOST_RECENT' | 'COMPANY_FIRST' | 'TEST_CORPUS'
    rag_min_confidence: number
    rag_clarification_mode: 'ASK' | 'LIMITED_ANSWER'
    rag_citations_required: boolean
    rag_require_evidence: boolean
    rag_agent_rules: string
    rag_internet_enabled: boolean
    rag_company_data_enabled: boolean
    rag_streaming_enabled: boolean
    rag_courtesy_enabled: boolean
    rag_greeting_formality: number
    rag_farewell_formality: number
    rag_greeting_intent: string
    rag_greeting_response: string
    rag_thanks_response: string
    rag_farewell_response: string
    rag_safety_response: string
    rag_unrecognized_response: string
    rag_blocked_terms: string
    rag_max_tokens: number
  }): Promise<ModuleConfiguration> {
    return (await axios.put('/knowledge/configuration/agent-policy', data)).data.data
  },
  async list(filters: Record<string, string | number>): Promise<{ items: Item[]; has_more: boolean }> {
    return (await axios.get('/knowledge/documents', { params: filters })).data.data
  },
  async get(id: number): Promise<Document> {
    return (await axios.get(`/knowledge/documents/${id}`)).data.data
  },
  async create(data: Content & Scope): Promise<{ document_id: number }> {
    return (await axios.post('/knowledge/documents', data)).data.data
  },
  async save(id: number, vid: number, revision: number, data: Content): Promise<void> {
    await axios.put(`/knowledge/documents/${id}/versions/${vid}`, { ...data, expected_revision: revision })
  },
  async scope(id: number, revision: number, data: Scope): Promise<void> {
    await axios.put(`/knowledge/documents/${id}/scope`, { ...data, expected_revision: revision })
  },
  async action(id: number, vid: number, revision: number, action: string, reason?: string): Promise<void> {
    await axios.post(`/knowledge/documents/${id}/versions/${vid}/${action}`, {
      expected_revision: revision,
      ...(reason ? { reason } : {}),
    })
  },
  async discardDraft(id: number, vid: number, revision: number): Promise<void> {
    await axios.post(`/knowledge/documents/${id}/versions/${vid}/discard`, {
      expected_revision: revision,
    })
  },
  async aiApproval(id: number, vid: number, revision: number, approve: boolean): Promise<AiDecision> {
    return (
      await axios.post(
        `/knowledge/documents/${id}/versions/${vid}/${approve ? 'approve-for-ai' : 'revoke-ai-approval'}`,
        { expected_revision: revision }
      )
    ).data.data
  },
  async attach(
    id: number,
    vid: number,
    revision: number,
    file: File,
    onProgress?: (loaded: number, total?: number) => void,
  ): Promise<void> {
    const form = new FormData()
    form.append('expected_revision', String(revision))
    form.append('file', file)
    await axios.post(`/knowledge/documents/${id}/versions/${vid}/resources`, form, {
      // The global client defaults to JSON. Make this upload contract explicit
      // so the browser preserves FormData and its boundary.
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => onProgress?.(event.loaded, event.total),
    })
  },
  async retireResource(id: number, vid: number, revision: number, rid: number): Promise<void> {
    await axios.post(`/knowledge/documents/${id}/versions/${vid}/resources/${rid}/retire`, {
      expected_revision: revision,
    })
  },
  async download(rid: number): Promise<Blob> {
    return (await axios.get(`/knowledge/resources/${rid}/download`, { responseType: 'blob' })).data
  },
  async audit(id: number, page: number, anchorId?: string | null): Promise<Audit[]> {
    return (
      await axios.get(`/knowledge/documents/${id}/audit`, {
        params: { page, ...(anchorId ? { anchor_id: anchorId } : {}) },
      })
    ).data.data
  },
  async permissions(): Promise<Permission[]> {
    return (await axios.get('/knowledge/permissions')).data.data
  },
  async grant(data: Omit<Permission, 'id'>): Promise<void> {
    await axios.put('/knowledge/permissions', data)
  },
}
